import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TokenGateway } from '../../../application/governance/ports';
import { TALLY_URL, WZEUS_ADDRESS, WZEUS_ETHERSCAN } from '../../../domain/governance/wrap';
import { useIsPhone } from '../../ByDevice';
import { C, EASE_OUT, Lettering, RedesignFonts, Socials, Buy } from '../../kit';
import { Chapter, ChapterBody, Container, Copy, DrawnMenu, FlexCol, HeroBg, ProgressBar, Slide, SwipeChapter, ThumbAction } from '../../m2';
import { FieldBox, FieldLabel, Foot, Grid, InkButton, Note, Page, PageTitle, Panel, Prose, SceneHeader, SectionTitle, SUB_NAV, TextButton, Wrap, WalletChip } from '../shared';
import { GovernancePageState, useGovernancePage } from './useGovernancePage';

/** /governance in the home page style: drawn header and ink panels on desktop, swipe chapters on a phone. */

export type GovernancePorts = { address: string | null; gateway: TokenGateway | null; connect: () => void; disconnect: () => void };

const SCENE_WIDE = '/redesign2/pages/gov-wide.webp';
const SCENE_TALL = '/redesign2/pages/gov-tall.webp';

const LEAD = "Pepe's Dog is run by its holders. Wrap your ZEUS into wZEUS and vote on what the community does next.";

const DAO_TEXT =
  'In October 2025 the community took over the project. Decisions are made by a DAO where every holder of wZEUS has a say, and every proposal and every vote is onchain.';

const DAO_POINTS = ['1 wZEUS is 1 vote.', 'Anyone can propose an idea and the holders decide.', 'Every vote is public and onchain, on Tally.', 'No single person controls ZEUS.'];

const WZEUS_TEXT =
  'To vote you need wZEUS, wrapped ZEUS. Wrapping is 1 to 1, costs only gas, and you can unwrap back to ZEUS at any time. wZEUS is not sold on Uniswap or anywhere else: you only get it by wrapping here.';

const SAFETY = [
  'You approve exactly the amount you wrap, never an unlimited amount.',
  'The wZEUS contract is verified on Etherscan, so anyone can read it.',
  'It only lets you deposit and withdraw, 1 to 1. Your ZEUS stays yours.',
  'Some wallets warn on every approval, even safe ones like this.',
];

const TallyLink: React.FC<{ big?: boolean; tone?: 'yellow' | 'white' }> = ({ big, tone }) => (
  <InkButton as="a" href={TALLY_URL} target="_blank" rel="noopener noreferrer" $big={big} $tone={tone}>
    VOTE ON TALLY
  </InkButton>
);

export const GovernanceScreen: React.FC<{ ports: GovernancePorts }> = ({ ports }) => {
  const s = useGovernancePage({ address: ports.address, gateway: ports.gateway });
  const phone = useIsPhone();
  return (
    <>
      <RedesignFonts />
      {phone ? <GovernanceMobile s={s} ports={ports} /> : <GovernanceDesktop s={s} ports={ports} />}
    </>
  );
};

/* ================================================================== desktop */

const GovernanceDesktop: React.FC<{ s: GovernancePageState; ports: GovernancePorts }> = ({ s, ports }) => (
  <Page data-page="governance" id="top">
    <SceneHeader scene={SCENE_WIDE} sky="#F6A21E" position="75% 30%" title={<PageTitle>GOVERNANCE</PageTitle>} lead={LEAD}>
      <TallyLink big />
      <InkButton as="a" href="#vote" $big $tone="white">
        GET YOUR VOTES
      </InkButton>
    </SceneHeader>

    <Wrap as="section" id="dao" aria-labelledby="dao-title">
      <Grid>
        <div>
          <SectionTitle id="dao-title">THE DAO</SectionTitle>
          <Prose>
            <p>{DAO_TEXT}</p>
            <ul>
              {DAO_POINTS.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Prose>
        </div>
        <Panel $tone="yellow" $tilt={1.4}>
          <Big>Proposals, votes and results all live on Tally.</Big>
          <TallyLink tone="white" />
        </Panel>
      </Grid>
    </Wrap>

    <Wrap as="section" id="vote" aria-labelledby="vote-title" style={{ paddingTop: 0 }}>
      <SectionTitle id="vote-title" tilt={1.5} tone="pink">
        GET YOUR VOTES
      </SectionTitle>
      <Grid $cols="minmax(0, 1.1fr) minmax(0, 0.9fr)">
        <Panel $tilt={-0.6}>
          <Prose style={{ marginBottom: 20 }}>
            <p style={{ margin: 0 }}>{WZEUS_TEXT}</p>
          </Prose>
          <WrapBox s={s} ports={ports} />
        </Panel>
        <Safety />
      </Grid>
    </Wrap>

    <Foot />
  </Page>
);

/* ================================================================== phone */

const GovernanceMobile: React.FC<{ s: GovernancePageState; ports: GovernancePorts }> = ({ s, ports }) => {
  const ref = useRef<HTMLDivElement>(null);
  const active = useActiveChapter(ref);
  return (
    <Container ref={ref} style={{ fontFamily: "'Ubuntu', sans-serif" }} data-page="governance">
      <ProgressBar active={active} total={4} />

      <Chapter className="chapter" data-index={0} bg="#F6A21E" id="top">
        <HeroBg src={SCENE_TALL} alt="" style={{ objectPosition: 'center 72%' }} />
        <ChapterBody style={{ padding: 'calc(env(safe-area-inset-top) + 96px) 18px 0' }}>
          <DrawnMenu items={SUB_NAV} />
          <h1 style={{ margin: '0 0 14px' }}>
            <Lettering $size="clamp(34px, 10.5vw, 52px)" $tilt={-3}>
              GOVERNANCE
            </Lettering>
          </h1>
          <Copy $big $shadow style={{ color: C.white, maxWidth: '24ch' }}>
            Pepe's Dog is run by its holders. 1 wZEUS is 1 vote.
          </Copy>
        </ChapterBody>
        <ThumbAction>
          <TallyLink big />
        </ThumbAction>
      </Chapter>

      <SwipeChapter id="dao" index={1} bg={C.paper}>
        <Slide $center bg="/redesign2/scenes/dock.webp" bgPosition="52% 50%">
          <MobileTitle>THE DAO</MobileTitle>
          <Copy $big>{DAO_TEXT}</Copy>
        </Slide>
        <Slide $center>
          <Prose>
            <ul>
              {DAO_POINTS.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Prose>
          <div style={{ alignSelf: 'center' }}>
            <TallyLink />
          </div>
        </Slide>
      </SwipeChapter>

      <SwipeChapter id="vote" index={2} bg={C.mist}>
        <Slide $center bg="/redesign2/scenes/pool.webp" bgPosition="73% 50%">
          <MobileTitle>GET YOUR VOTES</MobileTitle>
          <Copy $big>{WZEUS_TEXT}</Copy>
        </Slide>
        <Slide>
          <MobileTitle>YOUR WALLET</MobileTitle>
          <WrapBox s={s} ports={ports} />
        </Slide>
        <Slide $center>
          <Safety />
        </Slide>
      </SwipeChapter>

      <Chapter className="chapter" data-index={3} bg={C.ink} id="join">
        <HeroBg src="/redesign2/scenes/gang.webp" alt="" style={{ objectFit: 'contain', objectPosition: 'center 30%' }} />
        <ThumbAction>
          <FlexCol>
            <Buy big />
            <Socials color={C.white} />
            <small style={{ color: C.white, fontFamily: 'Ubuntu', marginTop: 10, fontWeight: 500 }}>© 2026 Pepe's Dog CTO</small>
          </FlexCol>
        </ThumbAction>
      </Chapter>
    </Container>
  );
};

function useActiveChapter(ref: React.RefObject<HTMLDivElement | null>) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(Number(e.target.getAttribute('data-index')))),
      { threshold: 0.5 }
    );
    ref.current?.querySelectorAll('section.chapter').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ref]);
  return active;
}

/* ================================================================== parts */

const MobileTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 style={{ margin: 0 }}>
    <Lettering $size="clamp(30px, 9vw, 40px)">{children}</Lettering>
  </h2>
);

const addToWallet = async () => {
  const eth = (window as unknown as { ethereum?: { request: (a: unknown) => Promise<unknown> } }).ethereum;
  if (!eth) return;
  try {
    await eth.request({
      method: 'wallet_watchAsset',
      params: { type: 'ERC20', options: { address: WZEUS_ADDRESS, symbol: 'wZEUS', decimals: 9, image: `${window.location.origin}/assets/img/logo.webp` } },
    });
  } catch {
    /* the wallet said no or does not support it */
  }
};

const WrapBox: React.FC<{ s: GovernancePageState; ports: GovernancePorts }> = ({ s, ports }) => {
  if (!s.connected)
    return (
      <div>
        <Copy style={{ marginBottom: 16 }}>Connect your wallet to see your ZEUS and wZEUS and wrap them.</Copy>
        <InkButton type="button" onClick={ports.connect}>
          Connect wallet
        </InkButton>
      </div>
    );
  const b = s.bal.data;
  return (
    <div>
      <WalletChip address={s.address!} onDisconnect={ports.disconnect} disabled={s.busy} />
      <Balances>
        <div>
          <span>ZEUS</span>
          <strong>{b ? s.fmt(b.zeus) : '...'}</strong>
        </div>
        <div>
          <span>wZEUS</span>
          <strong>{b ? s.fmt(b.wzeus) : '...'}</strong>
        </div>
        <div data-warn={!!b && b.votes === BigInt(0)}>
          <span>Votes</span>
          <strong>{b ? s.fmt(b.votes) : '...'}</strong>
        </div>
      </Balances>
      {s.bal.status === 'error' && (
        <Note tone="bad">
          Your balances could not load.{' '}
          <TextButton type="button" onClick={() => s.load()}>
            Try again
          </TextButton>
        </Note>
      )}

      {s.mustActivate && (
        <Activate>
          <p>Your wZEUS does not vote yet. Activate your votes once so they count. It costs a little gas.</p>
          <InkButton type="button" onClick={s.activate} disabled={s.busy}>
            Activate my votes
          </InkButton>
        </Activate>
      )}

      <Tabs role="tablist" aria-label="Wrap or unwrap">
        {(['wrap', 'unwrap'] as const).map((m) => (
          <button key={m} type="button" role="tab" aria-selected={s.mode === m} onClick={() => s.setMode(m)} disabled={s.busy}>
            {m === 'wrap' ? 'Wrap' : 'Unwrap'}
          </button>
        ))}
      </Tabs>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!s.busy) s.submit();
        }}
      >
        <FieldLabel htmlFor="wrap-amount">Amount of {s.mode === 'wrap' ? 'ZEUS' : 'wZEUS'}</FieldLabel>
        <FieldBox>
          <input id="wrap-amount" inputMode="decimal" value={s.amount} onChange={(e) => s.setAmount(e.target.value)} placeholder="0" autoComplete="off" disabled={s.busy} />
          <span className="side">
            <InkButton type="button" $tone="white" onClick={s.setMax} disabled={s.busy || !b} style={{ minHeight: 40, padding: '6px 14px', fontSize: 15 }}>
              MAX
            </InkButton>
          </span>
        </FieldBox>
        <Actions>
          <InkButton type="submit" disabled={s.busy}>
            {s.busy ? 'Working...' : s.action}
          </InkButton>
        </Actions>
      </form>
      {s.msg && <Note tone={s.msg.tone}>{s.msg.text}</Note>}
      <TextButton type="button" onClick={addToWallet} style={{ marginTop: 10 }}>
        Add wZEUS to my wallet
      </TextButton>
    </div>
  );
};

const SafetyList: React.FC = () => (
  <div>
    <SafetyTitle>IS THE APPROVAL SAFE?</SafetyTitle>
    <Prose>
      <ul>
        {SAFETY.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      <p>
        <a href={WZEUS_ETHERSCAN} target="_blank" rel="noopener noreferrer">
          See the wZEUS contract on Etherscan
        </a>
      </p>
    </Prose>
  </div>
);

const Safety: React.FC = () => (
  <Panel $tone="yellow" $tilt={1.2}>
    <SafetyList />
  </Panel>
);

/* ================================================================== styles */

const Big = styled.p`
  margin: 0 0 22px;
  font-family: 'GROBOLD', sans-serif;
  font-size: clamp(24px, 2.2vw, 32px);
  line-height: 1.2;
`;

const SafetyTitle = styled.p`
  margin: 0 0 14px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 24px;
`;

/** The three balances as price stickers: big figure, small caps label, each its own colour. */
const Balances = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 22px;
  div {
    min-width: 0;
    padding: 12px 10px 14px;
    text-align: center;
    background: ${C.white};
    border: 3px solid ${C.ink};
    border-radius: 16px 20px 14px 22px / 18px 14px 20px 16px;
    box-shadow: 3px 4px 0 ${C.ink};
  }
  div:nth-child(1) {
    background: ${C.yellow};
    transform: rotate(-1.5deg);
  }
  div:nth-child(2) {
    transform: rotate(1deg);
  }
  div:nth-child(3) {
    background: #a8e6b0;
    transform: rotate(-0.8deg);
  }
  div[data-warn='true'] {
    background: #ffb3de;
  }
  span {
    display: block;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  strong {
    display: block;
    margin-top: 2px;
    font-family: 'GROBOLD', sans-serif;
    font-size: clamp(26px, 2.6vw, 36px);
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** Zeus says it: a speech bubble from his head, with the one action inside. */
const Activate = styled.div`
  position: relative;
  margin: 0 0 22px 58px;
  padding: 14px 16px;
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 22px 26px 20px 28px / 24px 20px 26px 22px;
  box-shadow: 3px 4px 0 ${C.ink};
  &::before {
    content: '';
    position: absolute;
    left: -62px;
    top: 4px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    border: 3px solid ${C.ink};
    background: url('/images/zeus-avatar.jpg') center / cover;
  }
  &::after {
    content: '';
    position: absolute;
    left: -13px;
    top: 20px;
    width: 18px;
    height: 18px;
    background: ${C.white};
    border-left: 3px solid ${C.ink};
    border-bottom: 3px solid ${C.ink};
    transform: rotate(45deg);
  }
  p {
    margin: 0 0 12px;
    font-weight: 700;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  padding: 4px;
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  button {
    flex: 1;
    min-height: 46px;
    padding: 6px 20px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    font: 400 17px 'GROBOLD', sans-serif;
    color: ${C.ink};
    cursor: pointer;
    transition: background 200ms ${EASE_OUT};
  }
  button[aria-selected='true'] {
    background: ${C.yellow};
    box-shadow: inset 0 0 0 2.5px ${C.ink};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

